<template>
  <div>
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 items-center">
      <div class="md:col-span-1 lg:col-span-2">
        <SearchInput
          v-model="textInput"
          @input="onSearchByTabIndex"
          width="100%"
          :loading="loading"
          :label="`Buscar políticas`"
          hide-details
        />
      </div>

      <div class="md:col-span-1 lg:col-span-1">
        <outlined-button
          @click="openFilterModal()"
          :text="'Filtrar'"
          width="100%"
          height="56px"
          :prepend-icon="mdiFilter"
        />
      </div>

      <div class="md:col-span-2 lg:col-span-1">
        <v-tooltip location="top">
          <template #activator="{ props }">
            <div
              v-bind="props"
              class="w-full"
              :class="[!canCreatePolicy ? 'cursor-not-allowed' : '']"
            >
              <filled-button
                :disabled="!canCreatePolicy"
                @click="toggleCreateModal"
                :text="`Nova política`"
                width="100%"
                height="56px"
                :prepend-icon="mdiPlus"
                :class="[!canCreatePolicy ? 'pointer-events-none ' : '!bg-primary30']"
              />
            </div>
          </template>
          <span>{{
            canCreatePolicy ? 'Criar nova política' : 'Você não tem permissão para criar'
          }}</span>
        </v-tooltip>
      </div>
    </div>

    <div class="flex flex-col gap-6">
      <div class="overflow-x-auto rounded-lg shadow-sm border border-gray-200 min-h-[300px]">
        <template v-if="policyStore.isLoading">
          <DsPolicyTableSkeleton />
        </template>

        <ReportErrorState
          v-else-if="policyStore.hasError"
          title="Não foi possível carregar as políticas"
          :message="policyStore.loadError"
          @retry="policyStore.fetchDsPolicies()"
        />

        <template v-else>
          <DsPoliciesTab v-if="policyStore.policies.length > 0" :policies="policyStore.policies" />
          <EmptyList v-else title="Nenhuma política encontrada" class="py-12" />
        </template>
      </div>

      <v-pagination
        v-if="!policyStore.isLoading && policyStore.policies.length > 0"
        v-model="currentPage"
        :total-visible="5"
        :length="pageCount"
        density="comfortable"
        color="primary"
        @update:model-value="changePage"
        class="mt-4"
      />
    </div>

    <CreateDsPolicyModal
      v-if="showModalCreateUser"
      @close="toggleModalCreateUser"
      :modalActive="showModalCreateUser"
    />

    <FilterModal
      v-if="filterModalProps"
      :show-status-filter="true"
      :modalStateProp="filterModalProps"
      @close="closeFilterModal"
      @apply-filters="applyFilters"
      :applied-filters="appliedFilters"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import DsPoliciesTab from './ds.policies.tab.vue'
import DsPolicyTableSkeleton from '@/components/designSystem/Skeleton/DsPolicyTableSkeleton.vue'

import { useDsPolicyStore } from '@/pinia-store/useDsPolicyStore'
import { EModals, useModalStore } from '@/pinia-store/useModalStore'

import { mdiFilter, mdiPlus } from '@mdi/js'

import CreateDsPolicyModal from './components/CreateDsPolicyModal/Index.vue'
import EmptyList from '@/components/EmptyList.vue'
import ReportErrorState from '@/components/designSystem/ErrorState/ReportErrorState.vue'
import SearchInput from '@/components/designSystem/Inputs/SearchInput.vue'
import FilterModal from './components/FilterDsPoliciesModal/FilterModal.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import { useHeaderStore } from '@/pinia-store/useHeaderStore'
import PATHS from '@/router/paths'

const showModalCreateUser = ref(false)
const textInput = ref('')
const filterModalProps = ref(false)
const currentPage = ref(1)

const modalStore = useModalStore()
const policyStore = useDsPolicyStore()
// const companyStore = useCompanyStore()
const permissionsStore = usePermissionsStore()

const headerStore = useHeaderStore()

const loading = computed(() => policyStore.isLoading)

// TODO - Voltar após atualizar o schema
// const canCreatePolicy = computed(() => {
//   return permissionsStore.checkAnyRessources({
//     permission: AuthorizationPermissions.CompanyPolicyCreatingGranted,
//     resourceType: AuthorizationResourcesAndSubjects.Company
//   })
// })

const canCreatePolicy = computed(() => {
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.StationCreating,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  })
})

const pageCount = computed(() =>
  Math.ceil(policyStore.totalPolicies / policyStore.policyQuery.limit!)
)

const changePage = (page: number) => {
  currentPage.value = page
  policyStore.changePage(page)
}

onMounted(() => {
  headerStore.setTitle(
    'Política de Restrição',
    'Gerencie políticas de autorização de médicos, status e vínculos com empresas',
    { label: 'Políticas', route: PATHS.POLICIES_HOME }
  )

  if (policyStore.policyQuery) {
    policyStore.policyQuery.limit = 6
  }
  policyStore.fetchDsPolicies()
})

const onSearchByTabIndex = async () => {
  await policyStore.applyFilters({ ...policyStore.policyQuery, q: textInput.value })
}

const toggleCreateModal = () => {
  showModalCreateUser.value = !showModalCreateUser.value
  modalStore.toggleModal(EModals.CREATE_GROUP_MODAL)
}

const toggleModalCreateUser = () => {
  showModalCreateUser.value = false
}

const openFilterModal = () => {
  textInput.value = ''
  filterModalProps.value = true
}

const closeFilterModal = () => {
  filterModalProps.value = false
}

interface Filters {
  search: string
  companies: string[]
  isActive: 'active' | 'inactive' | ''
  authorizationStatus?: 'FULLY_AUTHORIZED' | 'PARTIALLY_AUTHORIZED' | ''
}

const appliedFilters = ref<Filters>({
  search: '',
  companies: [],
  isActive: '',
  authorizationStatus: ''
})

const applyFilters = (filters: Filters) => {
  appliedFilters.value = filters

  let isActiveValue: boolean | undefined = undefined
  if (filters.isActive === 'active') {
    isActiveValue = true
  } else if (filters.isActive === 'inactive') {
    isActiveValue = false
  }

  policyStore.applyFilters({
    q: appliedFilters.value.search,
    companyIds: appliedFilters.value.companies.join(','),
    isActive: isActiveValue,
    authorizationStatus: appliedFilters.value.authorizationStatus || undefined,
    limit: 6
  })

  filterModalProps.value = false
}
</script>

<style scoped>
.grid-layout {
  display: grid;
  grid-auto-columns: 1fr;
  grid-template-columns: 1fr;
  grid-template-rows: auto;
  gap: 0;
  grid-template-areas:
    'search'
    'table';
}

@media only screen and (max-width: 1024px) {
  .grid-layout {
    grid-template-areas:
      'search'
      'table'
      'buttons';
  }
}
</style>
